import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";

export const createAuthHeaders = () => {
  const token = localStorage.getItem("token") || "";
  if (!token) {
    throw new Error("No token found in localStorage");
  }
  return {
    Authorization: `Bearer ${token}`,
    "X-Use-New-Db": localStorage.getItem("useNewDb") || "false",
  };
};

// Pobiera jedną stronę odpraw z backendu (filtrowanie, sortowanie i paginacja po stronie serwera).
export const fetchDashboardPage = async (body, signal) => {
  const { data } = await axios.post(`${API_BASE_URL}/dashboard/odprawy`, body, {
    headers: createAuthHeaders(),
    signal,
  });
  return data;
};

const DEBOUNCE_MS = 300;
const AUTO_REFRESH_MS = 60000;

/**
 * Hook ładujący dane tabeli. Zmiana filtrów / sortowania / strony wywołuje (z opóźnieniem)
 * nowe zapytanie, a poprzednie dane zostają na ekranie do czasu odpowiedzi (bez migotania).
 * Co minutę dane odświeżają się po cichu, jeśli karta jest widoczna.
 */
export function useDashboardData({
  enabled,
  view,
  filters,
  sortBy,
  sortDir,
  page,
  pageSize,
  onUnauthorized,
}) {
  const [state, setState] = useState({
    rows: [],
    total: 0,
    facets: { rodzajTransportu: {}, statusRealizacji: {} },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const requestId = useRef(0);
  const abortRef = useRef(null);
  const hasLoaded = useRef(false);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  const load = useCallback(
    async ({ silent = false } = {}) => {
      const id = ++requestId.current;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (!silent) setLoading(true);
      try {
        const data = await fetchDashboardPage(
          { view, filters, sortBy, sortDir, page, pageSize },
          controller.signal,
        );
        if (id !== requestId.current) return;
        hasLoaded.current = true;
        setState({
          rows: data.rows || [],
          total: data.total || 0,
          facets: data.facets || { rodzajTransportu: {}, statusRealizacji: {} },
        });
        setError(null);
        setLastUpdated(new Date());
      } catch (err) {
        if (axios.isCancel(err) || err?.code === "ERR_CANCELED") return;
        if (id !== requestId.current) return;
        if (err?.response?.status === 401) {
          if (onUnauthorizedRef.current) onUnauthorizedRef.current();
          return;
        }
        console.error("Błąd pobierania danych dashboardu:", err);
        setError("Nie udało się pobrać danych. Spróbuj odświeżyć widok.");
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [view, filters, sortBy, sortDir, page, pageSize],
  );

  // Zmiana parametrów: pierwsze ładowanie od razu, kolejne z debounce (pisanie w polach filtrów).
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setTimeout(load, hasLoaded.current ? DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [enabled, load]);

  // Ciche odświeżanie w tle.
  useEffect(() => {
    if (!enabled) return undefined;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") load({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [enabled, load]);

  useEffect(() => () => abortRef.current && abortRef.current.abort(), []);

  const refresh = useCallback(() => load(), [load]);

  return { ...state, loading, error, lastUpdated, refresh };
}
